import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Save, Phone, Mail, Globe, MapPin, Upload, ImageIcon, Hash, FileText, LayoutTemplate } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompanySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    nombre: '',
    pais_origen: 'USA',
    pais_destino: 'Republica Dominicana',
    telefono: '',
    telefono_rd: '',
    direccion: '',
    email: '',
    website: '',
    terminos: '',
    prefijo_factura: 'MO',
    numero_inicio_factura: 100,
    plantilla_factura: 'clasica'
  });

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await api.get('/settings/company');
      const data = response.data;
      if (data.logo_url) {
        setLogoUrl(`${API_URL}${data.logo_url}?t=${Date.now()}`);
      }
      setFormData({
        nombre: data.nombre || '',
        pais_origen: data.pais_origen || 'USA',
        pais_destino: data.pais_destino || 'Republica Dominicana',
        telefono: data.telefono || '',
        telefono_rd: data.telefono_rd || '',
        direccion: data.direccion || '',
        email: data.email || '',
        website: data.website || '',
        terminos: data.terminos || '',
        prefijo_factura: data.prefijo_factura || 'MO',
        numero_inicio_factura: data.numero_inicio_factura || 100,
        plantilla_factura: data.plantilla_factura || 'clasica'
      });
    } catch (error) {
      console.error('Error loading company info');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put('/settings/company', formData);
      toast.success('Informacion de empresa actualizada');
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formPayload = new FormData();
    formPayload.append('file', file);

    try {
      const response = await api.post('/settings/company/logo', formPayload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setLogoUrl(`${API_URL}${response.data.logo_url}?t=${Date.now()}`);
      toast.success('Logo actualizado');
    } catch (error) {
      toast.error('Error al subir logo');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <Card className="shadow-xl border-2 border-blue-100">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-t-lg">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Building2 className="h-8 w-8" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Informacion de la Empresa</CardTitle>
                <p className="text-blue-100 text-base">Configuracion general para facturas y etiquetas</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Logo Section */}
            <div className="mb-8 p-6 border-2 border-dashed border-blue-300 rounded-xl bg-blue-50/50 text-center">
              <Label className="text-lg font-bold mb-4 block">Logo de la Empresa</Label>
              <div className="flex flex-col items-center gap-4">
                <div className="w-40 h-40 rounded-xl overflow-hidden bg-white border-2 border-gray-200 flex items-center justify-center shadow-md">
                  <img
                    src={logoUrl}
                    alt="Logo empresa"
                    className="max-w-full max-h-full object-contain"
                    data-testid="company-logo-preview"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="h-14 px-6 text-lg"
                  data-testid="upload-logo-btn"
                >
                  <Upload className="mr-2 h-5 w-5" />
                  {uploading ? 'Subiendo...' : 'Cambiar Logo'}
                </Button>
                <p className="text-base text-gray-500">Este logo aparece en facturas, etiquetas y el sistema</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Building2 className="h-4 w-4" /> Nombre de la Empresa
                  </Label>
                  <Input
                    data-testid="company-name-input"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    placeholder="DOMINICAN SHIPPING"
                    className="h-14 mt-1 text-xl font-bold"
                  />
                </div>

                <div>
                  <Label className="text-base font-semibold">Pais de Origen</Label>
                  <Input value={formData.pais_origen} onChange={(e) => setFormData({...formData, pais_origen: e.target.value})} className="h-12 mt-1 text-lg" />
                </div>
                <div>
                  <Label className="text-base font-semibold">Pais de Destino</Label>
                  <Input value={formData.pais_destino} onChange={(e) => setFormData({...formData, pais_destino: e.target.value})} className="h-12 mt-1 text-lg" />
                </div>

                <div>
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Phone className="h-4 w-4" /> Telefono USA
                  </Label>
                  <Input data-testid="company-phone-input" value={formData.telefono} onChange={(e) => setFormData({...formData, telefono: e.target.value})} placeholder="348-847-2228" className="h-12 mt-1 text-lg" />
                </div>
                <div>
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Phone className="h-4 w-4" /> Telefono RD
                  </Label>
                  <Input value={formData.telefono_rd} onChange={(e) => setFormData({...formData, telefono_rd: e.target.value})} placeholder="+1 (809) 460-2250" className="h-12 mt-1 text-lg" />
                </div>

                <div className="md:col-span-2">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <MapPin className="h-4 w-4" /> Direccion
                  </Label>
                  <Input data-testid="company-address-input" value={formData.direccion} onChange={(e) => setFormData({...formData, direccion: e.target.value})} placeholder="820 E 168TH ST BRONX NY 10459" className="h-12 mt-1 text-lg" />
                </div>

                <div>
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Email
                  </Label>
                  <Input value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="info@empresa.com" className="h-12 mt-1 text-lg" />
                </div>
                <div>
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Globe className="h-4 w-4" /> Sitio Web
                  </Label>
                  <Input value={formData.website || ''} onChange={(e) => setFormData({...formData, website: e.target.value})} placeholder="www.empresa.com" className="h-12 mt-1 text-lg" />
                </div>
              </div>

              {/* Invoice Numbering Section */}
              <div className="p-4 border-2 border-blue-200 rounded-xl bg-blue-50/30">
                <Label className="text-lg font-bold mb-3 block flex items-center gap-2">
                  <Hash className="h-5 w-5" /> Numeracion de Facturas
                </Label>
                <p className="text-base text-gray-500 mb-3">Ejemplo: {formData.prefijo_factura}-{formData.numero_inicio_factura}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-base font-semibold">Prefijo de Factura</Label>
                    <Input
                      data-testid="invoice-prefix-input"
                      value={formData.prefijo_factura}
                      onChange={(e) => setFormData({...formData, prefijo_factura: e.target.value.toUpperCase()})}
                      placeholder="MO"
                      className="h-12 mt-1 text-lg font-bold"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <Label className="text-base font-semibold">Numero de Inicio</Label>
                    <Input
                      data-testid="invoice-start-number-input"
                      type="number"
                      value={formData.numero_inicio_factura}
                      onChange={(e) => setFormData({...formData, numero_inicio_factura: parseInt(e.target.value) || 0})}
                      placeholder="100"
                      className="h-12 mt-1 text-lg font-bold"
                      min={1}
                    />
                  </div>
                </div>
              </div>

              {/* Invoice Template Selector with Previews */}
              <div className="p-4 border-2 border-indigo-200 rounded-xl bg-indigo-50/30">
                <Label className="text-lg font-bold mb-3 block flex items-center gap-2">
                  <LayoutTemplate className="h-5 w-5" /> Plantilla de Factura
                </Label>
                <p className="text-sm text-gray-500 mb-4">Seleccione el estilo visual de las facturas PDF de su empresa</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* CLASICA */}
                  <button
                    type="button"
                    data-testid="template-clasica-btn"
                    onClick={() => setFormData({ ...formData, plantilla_factura: 'clasica' })}
                    className={`relative rounded-xl border-2 text-left transition-all duration-200 overflow-hidden ${
                      formData.plantilla_factura === 'clasica'
                        ? 'border-indigo-600 ring-2 ring-indigo-400 shadow-lg scale-[1.02]'
                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                    }`}
                  >
                    {formData.plantilla_factura === 'clasica' && (
                      <span className="absolute top-2 right-2 z-10 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center shadow">
                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      </span>
                    )}
                    {/* Mini preview */}
                    <div className="bg-white p-2.5 pointer-events-none" data-testid="template-clasica-preview">
                      <div className="border border-gray-200 rounded" style={{ fontSize: '5px', lineHeight: '1.3', minHeight: '140px' }}>
                        {/* Header: logo | company | invoice# */}
                        <div className="flex items-center gap-1 p-1" style={{ borderBottom: '2px solid #00008B' }}>
                          <div className="w-5 h-5 bg-gray-300 rounded flex-shrink-0" />
                          <div className="flex-1">
                            <div className="font-bold" style={{ fontSize: '5.5px' }}>EMPRESA</div>
                            <div className="text-gray-400">USA - RD</div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold" style={{ fontSize: '8px', color: '#DC143C' }}>MO-101</div>
                          </div>
                        </div>
                        {/* Declaracion */}
                        <div className="bg-gray-100 text-center px-1 py-0.5 text-gray-500" style={{ fontSize: '4px' }}>DECLARACION JURADA</div>
                        {/* Two columns: Remitente | Destinatario */}
                        <div className="flex">
                          <div className="flex-1 border-r border-gray-200">
                            <div className="text-white text-center font-bold py-0.5" style={{ background: '#00008B', fontSize: '4.5px' }}>REMITENTE</div>
                            <div className="p-0.5 bg-purple-50">
                              <div className="font-bold">Juan Perez</div>
                              <div className="text-gray-400">Bronx, NY</div>
                            </div>
                          </div>
                          <div className="flex-1">
                            <div className="text-white text-center font-bold py-0.5" style={{ background: '#DC143C', fontSize: '4.5px' }}>DESTINATARIO</div>
                            <div className="p-0.5 bg-red-50">
                              <div className="font-bold">Maria Lopez</div>
                              <div className="text-gray-400">Santiago, RD</div>
                            </div>
                          </div>
                        </div>
                        {/* Products table */}
                        <div>
                          <div className="flex text-white font-bold px-1 py-0.5" style={{ background: '#00008B', fontSize: '4px' }}>
                            <span className="w-3">QTY</span><span className="flex-1">ARTICULO</span><span className="w-6 text-right">TOTAL</span>
                          </div>
                          <div className="flex px-1 py-0.5 border-b border-gray-100">
                            <span className="w-3">2</span><span className="flex-1">Caja mediana</span><span className="w-6 text-right">$40</span>
                          </div>
                          <div className="flex px-1 py-0.5 font-bold">
                            <span className="flex-1" /><span className="w-8 text-right">TOTAL:</span><span className="w-6 text-right" style={{ color: '#DC143C', fontSize: '6px' }}>$40</span>
                          </div>
                        </div>
                        {/* Pago */}
                        <div className="mx-1 mb-1">
                          <div className="text-white text-center font-bold py-0.5" style={{ background: '#00008B', fontSize: '4px' }}>RESUMEN DE PAGO</div>
                          <div className="bg-gray-50 px-1 py-0.5 flex justify-between"><span>Pago:</span><span>EFECTIVO</span></div>
                        </div>
                      </div>
                    </div>
                    <div className={`px-3 py-2.5 ${formData.plantilla_factura === 'clasica' ? 'bg-indigo-50' : 'bg-gray-50'}`}>
                      <span className="font-bold text-sm block">Clasica</span>
                      <span className="text-xs text-gray-500">Tablas con colores, formato tradicional</span>
                    </div>
                  </button>

                  {/* MODERNA */}
                  <button
                    type="button"
                    data-testid="template-moderna-btn"
                    onClick={() => setFormData({ ...formData, plantilla_factura: 'moderna' })}
                    className={`relative rounded-xl border-2 text-left transition-all duration-200 overflow-hidden ${
                      formData.plantilla_factura === 'moderna'
                        ? 'border-indigo-600 ring-2 ring-indigo-400 shadow-lg scale-[1.02]'
                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                    }`}
                  >
                    {formData.plantilla_factura === 'moderna' && (
                      <span className="absolute top-2 right-2 z-10 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center shadow">
                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      </span>
                    )}
                    <div className="bg-white p-2.5 pointer-events-none" data-testid="template-moderna-preview">
                      <div className="border border-gray-200 rounded overflow-hidden" style={{ fontSize: '5px', lineHeight: '1.3', minHeight: '140px' }}>
                        {/* Full-width colored header band */}
                        <div className="flex items-center gap-1 px-1.5 py-1.5" style={{ background: '#3b82f6' }}>
                          <div className="w-4 h-4 bg-white/30 rounded flex-shrink-0" />
                          <div className="flex-1 text-white">
                            <div className="font-bold" style={{ fontSize: '6px' }}>EMPRESA</div>
                            <div style={{ fontSize: '4px', opacity: 0.8 }}>Tel | Email</div>
                          </div>
                          <div className="text-right text-white">
                            <div style={{ fontSize: '4px', opacity: 0.7 }}>FACTURA</div>
                            <div className="font-bold" style={{ fontSize: '8px', color: '#f59e0b' }}>MO-101</div>
                          </div>
                        </div>
                        {/* Two-col customer - clean borders */}
                        <div className="flex gap-1 p-1.5">
                          <div className="flex-1 border-l-2 pl-1" style={{ borderColor: '#3b82f6' }}>
                            <div className="font-bold" style={{ color: '#3b82f6', fontSize: '4px' }}>DE:</div>
                            <div className="font-bold">Juan Perez</div>
                            <div className="text-gray-400">Bronx, NY</div>
                          </div>
                          <div className="flex-1 border-l-2 pl-1" style={{ borderColor: '#f59e0b' }}>
                            <div className="font-bold" style={{ color: '#3b82f6', fontSize: '4px' }}>PARA:</div>
                            <div className="font-bold">Maria Lopez</div>
                            <div className="text-gray-400">Santiago, RD</div>
                          </div>
                        </div>
                        {/* Products - minimal grid */}
                        <div className="px-1.5">
                          <div className="flex text-white font-bold px-1 py-0.5 rounded-sm" style={{ background: '#3b82f6', fontSize: '4px' }}>
                            <span className="w-3">QTY</span><span className="flex-1">DESC</span><span className="w-5 text-right">TOTAL</span>
                          </div>
                          <div className="flex px-1 py-0.5" style={{ borderBottom: '1px solid #e5e7eb' }}>
                            <span className="w-3">2</span><span className="flex-1">Caja mediana</span><span className="w-5 text-right">$40</span>
                          </div>
                          <div className="flex px-1 py-0.5 font-bold">
                            <span className="flex-1" /><span className="w-7 text-right">TOTAL:</span><span className="w-5 text-right" style={{ color: '#f59e0b', fontSize: '7px' }}>$40</span>
                          </div>
                        </div>
                        {/* Payment accent bar */}
                        <div className="mx-1.5 mt-1 mb-1.5 bg-gray-50 px-1 py-0.5 rounded" style={{ borderBottom: '2px solid #f59e0b' }}>
                          <span>Metodo: EFECTIVO</span>
                        </div>
                        {/* Footer */}
                        <div className="text-center text-gray-400 pb-1" style={{ fontSize: '3.5px' }}>EMPRESA | Tel | Direccion</div>
                      </div>
                    </div>
                    <div className={`px-3 py-2.5 ${formData.plantilla_factura === 'moderna' ? 'bg-indigo-50' : 'bg-gray-50'}`}>
                      <span className="font-bold text-sm block">Moderna</span>
                      <span className="text-xs text-gray-500">Limpia, minimalista, barra de acento</span>
                    </div>
                  </button>

                  {/* CORPORATIVA */}
                  <button
                    type="button"
                    data-testid="template-corporativa-btn"
                    onClick={() => setFormData({ ...formData, plantilla_factura: 'corporativa' })}
                    className={`relative rounded-xl border-2 text-left transition-all duration-200 overflow-hidden ${
                      formData.plantilla_factura === 'corporativa'
                        ? 'border-indigo-600 ring-2 ring-indigo-400 shadow-lg scale-[1.02]'
                        : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                    }`}
                  >
                    {formData.plantilla_factura === 'corporativa' && (
                      <span className="absolute top-2 right-2 z-10 w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center shadow">
                        <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                      </span>
                    )}
                    <div className="bg-white p-2.5 pointer-events-none" data-testid="template-corporativa-preview">
                      <div className="border border-gray-200 rounded overflow-hidden" style={{ fontSize: '5px', lineHeight: '1.3', minHeight: '140px' }}>
                        {/* Top accent line */}
                        <div style={{ height: '3px', background: '#DC143C' }} />
                        {/* Header: logo | center | boxed invoice */}
                        <div className="flex items-center gap-1 p-1">
                          <div className="w-4 h-4 bg-gray-300 rounded flex-shrink-0" />
                          <div className="flex-1 text-center">
                            <div className="font-bold" style={{ fontSize: '5.5px' }}>EMPRESA</div>
                            <div className="text-gray-400" style={{ fontSize: '3.5px' }}>Direccion | Tel | Email</div>
                          </div>
                          <div className="border-2 rounded px-1 py-0.5 text-center" style={{ borderColor: '#00008B' }}>
                            <div style={{ fontSize: '3.5px' }}>FACTURA No.</div>
                            <div className="font-bold" style={{ fontSize: '7px', color: '#DC143C' }}>MO-101</div>
                            <div className="text-gray-400" style={{ fontSize: '3.5px' }}>27/03/2026</div>
                          </div>
                        </div>
                        {/* Thin primary line */}
                        <div style={{ height: '1.5px', background: '#00008B', margin: '0 4px' }} />
                        {/* Customer blocks side-by-side */}
                        <div className="flex gap-1 p-1">
                          <div className="flex-1 border rounded overflow-hidden" style={{ borderColor: '#ccc' }}>
                            <div className="text-white text-center font-bold py-0.5" style={{ background: '#00008B', fontSize: '4px' }}>REMITENTE</div>
                            <div className="p-0.5" style={{ fontSize: '4.5px' }}>
                              <div><b>Nombre:</b> Juan Perez</div>
                              <div><b>Dir:</b> Bronx, NY</div>
                              <div><b>Tel:</b> 718-555-0001</div>
                            </div>
                          </div>
                          <div className="flex-1 border rounded overflow-hidden" style={{ borderColor: '#ccc' }}>
                            <div className="text-white text-center font-bold py-0.5" style={{ background: '#DC143C', fontSize: '4px' }}>DESTINATARIO</div>
                            <div className="p-0.5" style={{ fontSize: '4.5px' }}>
                              <div><b>Nombre:</b> Maria Lopez</div>
                              <div><b>Dir:</b> Santiago, RD</div>
                              <div><b>Tel:</b> 809-555-0002</div>
                            </div>
                          </div>
                        </div>
                        {/* Products with # column and grid */}
                        <div className="px-1">
                          <div className="flex text-white font-bold px-0.5 py-0.5" style={{ background: '#00008B', fontSize: '4px' }}>
                            <span className="w-2">#</span><span className="w-3">QTY</span><span className="flex-1">DESC</span><span className="w-5 text-right">P.U.</span><span className="w-5 text-right">TOTAL</span>
                          </div>
                          <div className="flex px-0.5 py-0.5 border-b border-gray-200">
                            <span className="w-2">1</span><span className="w-3">2</span><span className="flex-1">Caja med.</span><span className="w-5 text-right">$20</span><span className="w-5 text-right">$40</span>
                          </div>
                          <div className="flex px-0.5 py-0.5 font-bold" style={{ borderTop: '2px solid #00008B' }}>
                            <span className="flex-1" /><span className="w-7 text-right">TOTAL:</span><span className="w-5 text-right" style={{ color: '#DC143C', fontSize: '6px' }}>$40</span>
                          </div>
                        </div>
                        {/* Payment grid */}
                        <div className="mx-1 mb-0.5 grid grid-cols-4 bg-gray-50 border border-gray-200 rounded" style={{ fontSize: '4px' }}>
                          <div className="font-bold px-0.5 py-0.5 border-r border-gray-200">Tipo:</div>
                          <div className="px-0.5 py-0.5 border-r border-gray-200">CONTADO</div>
                          <div className="font-bold px-0.5 py-0.5 border-r border-gray-200">Metodo:</div>
                          <div className="px-0.5 py-0.5">EFECTIVO</div>
                        </div>
                        {/* Footer bar */}
                        <div className="text-white text-center py-0.5 mt-0.5" style={{ background: '#00008B', fontSize: '3.5px' }}>EMPRESA | Tel | Gracias por su preferencia</div>
                      </div>
                    </div>
                    <div className={`px-3 py-2.5 ${formData.plantilla_factura === 'corporativa' ? 'bg-indigo-50' : 'bg-gray-50'}`}>
                      <span className="font-bold text-sm block">Corporativa</span>
                      <span className="text-xs text-gray-500">Profesional, estructurada, detallada</span>
                    </div>
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-base font-semibold">Terminos y Condiciones (para facturas)</Label>
                <textarea
                  data-testid="company-terms-input"
                  value={formData.terminos || ''}
                  onChange={(e) => setFormData({...formData, terminos: e.target.value})}
                  className="w-full mt-1 p-3 border-2 rounded-lg min-h-[100px] text-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                  placeholder="Terminos y condiciones que aparecen en las facturas..."
                />
              </div>

              <Button
                data-testid="save-company-btn"
                type="submit"
                size="lg"
                disabled={saving}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 h-14 text-xl"
              >
                <Save className="mr-2 h-6 w-6" />
                {saving ? 'Guardando...' : 'Guardar Configuracion'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompanySettings;
